// PricingCard.tsx
"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { PricingCardData } from "./pricing";

interface PricingCardProps {
  card: PricingCardData;
}

const PricingCard: React.FC<PricingCardProps> = ({ card }) => {
  const { t } = useTranslation("pricing");
  const getBadges = () => {
    const badges = [];
    const title = card.title.toLowerCase();

    if (
      title.includes("daily") ||
      title.includes("voucher") ||
      title.includes("monthly")
    ) {
      badges.push(
        {
          label: t("common.badges.individuals"),
          color: "bg-blue-100 text-blue-700",
        },
        {
          label: t("common.badges.southworking"),
          color: "bg-green-100 text-green-700",
        }
      );
    }

    if (title.includes("business")) {
      badges.push({
        label: t("common.badges.companies"),
        color: "bg-purple-100 text-purple-700",
      });
    }

    return badges;
  };

  const badges = getBadges();

  return (
    <div className="group relative h-full border border-gray-200 rounded-2xl bg-white p-6 text-center shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col">
      {badges.length > 0 && (
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 z-10">
          {badges.map((badge) => (
            <span
              key={badge.label}
              className={`text-xs font-medium px-2.5 py-1 rounded-md whitespace-nowrap ${badge.color}`}
            >
              {badge.label}
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-col h-full gap-6">
        {card.imageUrl && (
          <div className="px-4">
            <Image
              width={140}
              height={140}
              src={card.imageUrl}
              alt={card.title}
              className="mx-auto object-contain transition-transform group-hover:scale-105 duration-300"
            />
          </div>
        )}

        <div className="flex flex-col gap-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">{card.title}</h3>
            <p className="text-gray-600 text-sm">{card.description}</p>
          </div>

          {(card.capacity || card.location) && (
            <div className="text-sm text-gray-700 space-y-2">
              {card.capacity && (
                <p>
                  <span className="text-gray-500">{t("common.capacity")}</span>{" "}
                  <span className="font-medium">{card.capacity}</span>
                </p>
              )}
              {card.location && (
                <p>
                  <span className="text-gray-500">{t("common.location")}</span>{" "}
                  <span className="font-medium">{card.location}</span>
                </p>
              )}
            </div>
          )}

          <div>
            {card.starting_at && (
              <p className="text-xs text-gray-500 uppercase tracking-wide">
                {t("common.startingAt")}
              </p>
            )}

            <div className="flex items-baseline justify-center gap-x-2 mt-1">
              {card.originalPrice && (
                <del className="text-xl font-medium text-gray-400">
                  {card.originalPrice}
                </del>
              )}
              <span className="text-3xl font-bold text-gray-900">
                {card.price}
              </span>
            </div>

            {card.type === "spazio" && /\d/.test(card.price) && (
              <span className="text-xs font-normal text-gray-500">
                {t("common.vatIncluded")}
              </span>
            )}
          </div>

          <ul className="space-y-3 text-sm/6 text-gray-600 mt-4">
            {card.features.map((feature) => (
              <li key={feature} className="flex gap-x-3">
                <FontAwesomeIcon
                  icon={faCheck}
                  className="text-green-600 mt-0.5 flex-shrink-0"
                />
                <span className="text-left">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {card.fondazioneNote && (
          <Link href="/coworking/buzz-card" className="mt-auto block">
            <div className="group/buzz flex items-center gap-4 bg-yellow-300 rounded-xl p-3 text-yellow-900 shadow-sm hover:bg-yellow-400 hover:shadow-md transition-all duration-200">
              <Image
                width={50}
                height={50}
                src="/queen.png"
                alt="Buzz Card"
                className="shrink-0 transition-transform duration-300 group-hover/buzz:scale-110"
              />
              <p className="text-left leading-snug font-semibold text-sm">
                {t("common.buzzCardNote")}
              </p>
            </div>
          </Link>
        )}
      </div>
    </div>
  );
};

export default PricingCard;
